import { Box, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { getPieChartColorsByValues } from '../../themes/tokens/data-chart-tokens';

/** 饼图布局：`default` 为卡片内营业饼图；`dialog` 为对比弹窗侧（无内置图例、显示占比）。 */
export type PieChartOfBusinessLayout = 'default' | 'dialog';

/** `layout="dialog"` 时扇区文字：`percent` 仅百分比；`labelPercent` 为「名称 百分比%」（集团支付方式占比）。 */
export type PieChartDialogDataLabelMode = 'percent' | 'labelPercent';

/**
 * 营业数据饼图（Apex）。
 * @param data - 系列数值（金额或占比基数）
 * @param labels - 扇区标签
 * @param titleText - Apex 标题文案；`layout="dialog"` 时不展示标题时可传空字符串
 * @param layout - 布局形态，默认 `default`
 * @param colors - 可选扇区色（须与 `data.length` 一致）；不传则按 value 名次 {@link getPieChartColorsByValues} 生成
 * @param height - `layout="dialog"` 时图表高度（px），默认 280
 * @param dialogDataLabelMode - `dialog` 扇区标签格式，默认 `percent`
 * @param dialogDataLabelFontSize - `dialog` 扇区标签字号（如 `'16px'`），默认 `'14px'`
 * @param containerSx - 外层 `Box` 样式覆盖
 */
const PieChartOfBusinessData = (params: {
    data: number[];
    labels: string[];
    titleText: string;
    layout?: PieChartOfBusinessLayout;
    colors?: string[];
    height?: number;
    dialogDataLabelMode?: PieChartDialogDataLabelMode;
    dialogDataLabelFontSize?: string;
    containerSx?: SxProps<Theme>;
}) => {
    const theme = useTheme();
    const layout = params.layout ?? 'default';
    /** 扇区填充色：调用方传入或与标签维度推导 */
    const seriesColors = useMemo(() => {
        if (params.colors && params.colors.length === params.data.length) {
            return params.colors;
        }
        return getPieChartColorsByValues(params.data, params.labels);
    }, [params.colors, params.data, params.labels.join('|')]);

    const series = params.data;
    const dialogHeight = params.height ?? 280;

    /** dialog：外侧自建图例，扇区上显示占比整数 */
    const isDialog = layout === 'dialog';

    const options: ApexCharts.ApexOptions = {
        labels: params.labels,
        colors: seriesColors,
        title: isDialog
            ? {
                  text: '',
                  align: 'center',
                  style: { color: theme.palette.text.primary },
              }
            : {
                  text: params.titleText,
                  align: 'center',
                  style: {
                      color: theme.palette.text.primary,
                  },
              },
        legend: isDialog
            ? { show: false }
            : {
                  position: 'bottom',
                  labels: {
                      colors: [theme.palette.text.primary],
                  },
              },
        chart: {
            width: '100%',
            type: 'pie',
            toolbar: isDialog
                ? { show: false }
                : {
                      show: true,
                      offsetY: 0,
                      offsetX: 0,
                      export: {
                          csv: {
                              filename: params.titleText,
                          },
                          svg: {
                              filename: params.titleText,
                          },
                          png: {
                              filename: params.titleText,
                          },
                      },
                  },
        },
        ...(isDialog
            ? {
                  dataLabels: {
                      enabled: true,
                      formatter:
                          params.dialogDataLabelMode === 'labelPercent'
                              ? (val: number, opts?: unknown) => {
                                    const w = (
                                        opts as {
                                            w?: { config: { labels: string[] } };
                                            seriesIndex?: number;
                                        }
                                    ).w;
                                    const seriesIndex =
                                        (opts as { seriesIndex?: number }).seriesIndex ?? 0;
                                    const label =
                                        w?.config?.labels?.[seriesIndex] ?? '';
                                    return `${label} ${Math.round(val)}%`;
                                }
                              : (value: number) => `${Math.round(value)}%`,
                      style: {
                          colors: ['var(--fill-neutral-100)'],
                          fontSize: params.dialogDataLabelFontSize ?? '14px',
                      },
                  },
              }
            : {}),
    };

    const defaultBoxSx: SxProps<Theme> =
        layout === 'dialog'
            ? {
                  width: dialogHeight,
                  height: dialogHeight,
                  minWidth: dialogHeight,
                  maxWidth: dialogHeight,
                  flexShrink: 0,
              }
            : { minWidth: 250, maxWidth: 460, height: '100%', width: '100%' };

    return (
        <Box sx={{ ...defaultBoxSx, ...params.containerSx }}>
            <ReactApexChart
                options={options}
                series={series}
                type="pie"
                height={layout === 'dialog' ? dialogHeight : undefined}
            />
        </Box>
    );
};

export default PieChartOfBusinessData;
